import { SquadupOptions } from "./squadup-options"
import {
  SQUADUP_EVENT_TYPES,
  SquadupDispatchedEvent,
  SquadupEventHandler,
  SquadupEventName,
  SquadupEvents,
} from "./squadup-events"
import { createSquadupState, SquadupState } from "./squadup-state"
import { ADDRESS_ORDER_QUESTIONS } from "../squadup=questions-options"

const DEBUG = false

const SQUADUP_DEFAULT_OPTIONS: SquadupOptions = {
  onDomReady: false,
  title: "Select Tickets Below",
  root: "squadup-root",
  userId: [5347240],
  brandingPosition: "none",
  collapseTicketDescription: false,
  flyerList: false,
  ticketGuardianEnabled: false,
  shoppingCartEnabled: true,
  descriptionEnabled: false,
  successCallback: () => {},
  ...ADDRESS_ORDER_QUESTIONS,
}

export class SquadupController {
  private static main: SquadupController
  private openTimer?: NodeJS.Timeout

  private eventHandlers = new Map<SquadupEventName, Set<SquadupEventHandler>>()

  state!: SquadupState

  constructor() {
    if (env.client) {
      this.state = window.__squadupController?.state ?? createSquadupState()
      this.install()
    }
  }

  get options() {
    return window.squadup
  }

  static getMain() {
    if (!SquadupController.main) {
      SquadupController.main = new SquadupController()
    }
    return SquadupController.main
  }

  private install() {
    // Set the current controller, for the event handlers
    window.__squadupController = this
    if (window.__squadupInstalled) return

    // Set initial configuration
    window.squadup = { ...SQUADUP_DEFAULT_OPTIONS }

    // Register event handlers
    // Events are passed to the active controller
    // Controller will not change except for in HMR dev mode
    for (const eventName in SQUADUP_EVENT_TYPES) {
      document.addEventListener(eventName, (e) => {
        if (e instanceof CustomEvent) {
          window.__squadupController.handleEvent(eventName as SquadupEventName, e.detail)
        }
      })
    }

    // Create root div
    const root = document.createElement("div")
    root.id = "squadup-root"
    root.appendChild(document.createElement("div"))
    document.body.appendChild(root)
    window.__squadupRoot = root

    window.__squadupLoadPromise = new Promise<void>(async (resolve) => {
      // Install Squadup script
      await new Promise<void>((resolve) => {
        if (document.readyState === "complete") {
          console.log("readyState already complete")
          resolve()
        } else {
          document.addEventListener("readystatechange", () => {
            console.log("readyState updated to", document.readyState)
            if (document.readyState === "complete") {
              console.log("readyState is now complete")
              resolve()
            }
          })
        }
      })

      console.log("loading squadup")
      const script = document.createElement("script")
      // script.src = env.themePath + "/squadup.js"
      script.src = "https://embed.squadup.com/main-v2.min.js"
      script.onload = () => {
        setTimeout(() => {
          console.log("Loaded SquadUp")
          resolve()
        }, 10)
      }
      document.body.appendChild(script)
    })

    this.subscribe("viewChanged", (name, data) => {
      if (data.currentView === "eventList") {
        this.close()
      }
    })

    // Observe the root element, looking for the loading spinner
    const loadingObserver = new MutationObserver((mutations) => {
      const spinner = window.__squadupRoot.querySelector(".squadup-checkout-embed-spinner") as HTMLDivElement | null
      this.state.loading = !!spinner?.classList.contains("showing")
      this.state.applyHacks?.(window.__squadupRoot)
    })
    loadingObserver.observe(window.__squadupRoot, {
      childList: true,
      subtree: true,
    })

    // Mark as installed
    window.__squadupInstalled = true

    if (DEBUG) {
      const originalDispatch = document.dispatchEvent
      document.dispatchEvent = function (event) {
        if (event instanceof CustomEvent) {
          const name = event.type
          const data = event.detail
          // console.log("Squadup event:", name, { ...data })
          // if (name in SQUADUP_EVENT_TYPES) {
          //   const schema = SQUADUP_EVENT_TYPES[name as keyof typeof SQUADUP_EVENT_TYPES]
          //   const result = schema.safeParse(data)
          //   if (!result.success) {
          //     console.warn(`Squadup event '${name}' didn't match the event data:`, data, result.error)
          //   }
          // } else {
          //   console.warn(`Unknown Squadup event: ${name}`)
          // }
        }
        return originalDispatch.call(document, event)
      }
    }
  }

  get element() {
    return window.__squadupRoot
  }

  private handleEvent<TName extends SquadupEventName>(eventName: TName, data: SquadupEvents[TName]) {
    // Update state
    console.log("Event:", eventName, data)
    this.state.dispatchEvent({
      type: eventName,
      data,
    } as SquadupDispatchedEvent)

    // Notify event listeners
    if (this.eventHandlers.has(eventName)) {
      for (const handler of this.eventHandlers.get(eventName)!) {
        handler(eventName, data)
      }
    }
  }

  subscribe<TName extends SquadupEventName>(eventName: TName, handler: SquadupEventHandler) {
    if (!this.eventHandlers.has(eventName)) {
      this.eventHandlers.set(eventName, new Set())
    }
    this.eventHandlers.get(eventName)!.add(handler)
    return () => {
      this.eventHandlers.get(eventName)!.delete(handler)
    }
  }

  async init() {
    await window.__squadupLoadPromise
    document.dispatchEvent(new CustomEvent("createSquadupEmbed"))
  }

  async open(opts: Partial<SquadupOptions>) {
    clearTimeout(this.openTimer!)
    await window.__squadupLoadPromise
    this.close()
    Object.assign(window.squadup, {
      ...SQUADUP_DEFAULT_OPTIONS,
      ...opts,
    })
    this.openTimer = setTimeout(() => {
      console.log("Open", opts)
      this.state.hasManuallyOpened = true
      this.state.open = true
      document.dispatchEvent(new CustomEvent("createSquadupEmbed"))
    }, 100)
  }

  close() {
    clearTimeout(this.openTimer!)
    console.log("Closing")
    document.dispatchEvent(new CustomEvent("destroySquadupEmbed"))
  }

  async openEvent(eventId: number) {
    await this.open({
      eventId,
      showCheckout: false,
    })
  }
}

declare global {
  interface Window {
    squadup: SquadupOptions
    __squadupInstalled: boolean
    __squadupController: SquadupController
    __squadupLoadPromise: Promise<void>
    __squadupRoot: HTMLDivElement
  }
}
